/**
 * Bucket Helper Utilities for Payment Reminder Aging
 * Based on the visual guide for bucket categorization
 */

export interface BucketColorConfig {
  bg: string;
  text: string;
  border: string;
  headerBg: string;
  severity: 'safe' | 'warning' | 'danger' | 'critical';
  label: string;
}

/**
 * Get color configuration for a bucket based on its code
 */
export const getBucketColorConfig = (bucketCode: string): BucketColorConfig => {
  switch (bucketCode) {
    case 'NOT_DUE':
      return {
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        border: 'border-emerald-200',
        headerBg: '#D1FAE5', // emerald-100
        severity: 'safe',
        label: 'Not Due',
      };

    case 'GETTING_DUE':
      return {
        bg: 'bg-orange-50',
        text: 'text-orange-700',
        border: 'border-orange-200',
        headerBg: '#FED7AA', // orange-200
        severity: 'warning',
        label: 'Getting Due',
      };

    case 'OVERDUE_0_15':
      return {
        bg: 'bg-red-50',
        text: 'text-red-600',
        border: 'border-red-200',
        headerBg: '#FCA5A5', // red-300
        severity: 'danger',
        label: 'Overdue 0-15',
      };

    case 'OVERDUE_16_30':
      return {
        bg: 'bg-red-100',
        text: 'text-red-700',
        border: 'border-red-300',
        headerBg: '#EF4444', // red-500
        severity: 'danger',
        label: 'Overdue 16-30',
      };

    case 'OVERDUE_31_60':
      return {
        bg: 'bg-red-100',
        text: 'text-red-800',
        border: 'border-red-400',
        headerBg: '#DC2626', // red-600
        severity: 'critical',
        label: 'Overdue 31-60',
      };

    case 'OVERDUE_61_90':
      return {
        bg: 'bg-red-200',
        text: 'text-red-900',
        border: 'border-red-500',
        headerBg: '#991B1B', // red-800
        severity: 'critical',
        label: 'Overdue 61-90',
      };

    case 'OVERDUE_91_180':
      return {
        bg: 'bg-red-200',
        text: 'text-red-950',
        border: 'border-red-600',
        headerBg: '#7F1D1D', // red-900
        severity: 'critical',
        label: 'Overdue 91-180',
      };

    case 'OVERDUE_181_365':
      return {
        bg: 'bg-red-300',
        text: 'text-red-950',
        border: 'border-red-700',
        headerBg: '#450A0A', // red-950
        severity: 'critical',
        label: 'Overdue 181-365',
      };

    case 'OVERDUE_365_PLUS':
      return {
        bg: 'bg-red-300',
        text: 'text-red-950',
        border: 'border-red-800',
        headerBg: '#1F0101', // almost black with red tint
        severity: 'critical',
        label: 'Overdue 365+',
      };

    default:
      return {
        bg: 'bg-gray-50',
        text: 'text-gray-700',
        border: 'border-gray-200',
        headerBg: '#F3F4F6', // gray-100
        severity: 'safe',
        label: 'Unknown',
      };
  }
};

/**
 * Get severity icon for bucket
 */
export const getBucketSeverityIcon = (bucketCode: string): string => {
  const config = getBucketColorConfig(bucketCode);

  switch (config.severity) {
    case 'safe':
      return '🟢';
    case 'warning':
      return '🟡';
    case 'danger':
      return '🔴';
    case 'critical':
      return '⚫';
    default:
      return '⚪';
  }
};

/**
 * Calculate total overdue amount for a customer
 */
export const calculateTotalOverdue = (
  aging: Record<string, number>,
): number => {
  const overdueBuckets = [
    'OVERDUE_0_15',
    'OVERDUE_16_30',
    'OVERDUE_31_60',
    'OVERDUE_61_90',
    'OVERDUE_91_180',
    'OVERDUE_181_365',
    'OVERDUE_365_PLUS',
  ];

  return overdueBuckets.reduce((total, bucket) => {
    return total + (aging[bucket] || 0);
  }, 0);
};

/**
 * Calculate bucket summary statistics
 */
export const calculateBucketSummary = (
  customers: { aging: Record<string, number> }[],
  bucketCode: string,
): { total: number; count: number } => {
  let total = 0;
  let count = 0;

  customers.forEach(customer => {
    const amount = customer.aging[bucketCode] || 0;
    if (amount > 0) {
      total += amount;
      count++;
    }
  });

  return { total, count };
};

/**
 * Get bucket group (for visual grouping)
 */
export const getBucketGroup = (
  bucketCode: string,
): 'not-due' | 'getting-due' | 'overdue' => {
  if (bucketCode === 'NOT_DUE') return 'not-due';
  if (bucketCode === 'GETTING_DUE') return 'getting-due';
  return 'overdue';
};

/**
 * Format bucket label with emoji
 */
export const formatBucketLabel = (
  bucketCode: string,
  bucketLabel: string,
): string => {
  const icon = getBucketSeverityIcon(bucketCode);
  return `${icon} ${bucketLabel}`;
};
